import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getBottomBoundary, getMenuBarBottom } from '@/lib/editor/floating-canvas-chrome-bounds'
import { maxFittingListHeight } from '../helpers/clamp-list-height'

/** Space kept between the popover's bottom edge and the bottom of the live area. */
const BOTTOM_MARGIN = 8

/**
 * The tallest the color list can render so the whole popover fits between the menu bar and the
 * bottom of the live area. Attach the returned ref to the popover's root. Re-measured when the
 * window resizes and when the popover itself resizes (the list opening, search results), so a
 * shorter window shrinks the list instead of pushing the popover up over the menu bar.
 */
export function useFitListHeight(
  listRef: React.RefObject<HTMLDivElement | null>
): [(element: HTMLElement | null) => void, number] {
  const [maxHeight, setMaxHeight] = useState(Infinity)
  const cleanupRef = useRef<(() => void) | null>(null)

  const ref = useCallback(
    (element: HTMLElement | null) => {
      cleanupRef.current?.()
      cleanupRef.current = null

      if (!element) return

      const measure = () => {
        const chromeHeight = element.offsetHeight - (listRef.current?.offsetHeight ?? 0)

        setMaxHeight(
          maxFittingListHeight({
            top: getMenuBarBottom(),
            bottom: getBottomBoundary() - BOTTOM_MARGIN,
            chromeHeight,
          })
        )
      }

      measure()
      window.addEventListener('resize', measure)

      const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)

      observer?.observe(element)
      cleanupRef.current = () => {
        window.removeEventListener('resize', measure)
        observer?.disconnect()
      }
    },
    [listRef]
  )

  useEffect(() => () => cleanupRef.current?.(), [])

  return [ref, maxHeight]
}
